import React, { useContext } from 'react'
import { Calendar } from 'react-date-range'
import { useSelector } from 'react-redux'
import Icon from 'icons/Icon'
import useOutsideClick from 'utils/useOutsideClick'
import { useDateLocale } from 'hooks/useDateLocale'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import './styles/index.scss'

const DateBirthV2 = ({ formik, contextValue, disabled = false }) => {
  const { regFields } = useContext(contextValue)
  const { values, setFieldValue, setFieldTouched, errors, touched } = formik
  const { ref, isVisible, setIsVisible } = useOutsideClick(false)
  const hasError = errors?.dateOfBirth && touched?.dateOfBirth
  const { languageData } = useSelector((state) => state.language)
  const dateLocale = useDateLocale()

  // Parse the dateOfBirth value to a Date object
  const parseDate = () => {
    const raw = values?.dateOfBirth
    if (!raw) return null

    // If it's already a Date object
    if (raw instanceof Date && !isNaN(raw.getTime())) {
      return raw
    }

    // Try to parse string date
    const str = String(raw).trim()
    if (!str) return null

    // Try parsing various formats
    let parts = []
    if (str.includes('/')) {
      parts = str.split('/')
    } else if (str.includes('-')) {
      parts = str.split('-')
    }

    if (parts.length >= 3) {
      // Handle YYYY/MM/DD format
      if (/^\d{4}$/.test(parts[0])) {
        const year = parseInt(parts[0])
        const month = parseInt(parts[1]) - 1 // JavaScript months are 0-indexed
        const day = parseInt(parts[2])
        const date = new Date(year, month, day)
        return isNaN(date.getTime()) ? null : date
      }
      // Handle DD/MM/YYYY format
      if (/^\d{4}$/.test(parts[2])) {
        const year = parseInt(parts[2])
        const month = parseInt(parts[1]) - 1
        const day = parseInt(parts[0])
        const date = new Date(year, month, day)
        return isNaN(date.getTime()) ? null : date
      }
    }

    // Try parsing as standard date string
    const date = new Date(str)
    return isNaN(date.getTime()) ? null : date
  }

  const handleDateChange = (date) => {
    if (date) {
      // Format date as YYYY/MM/DD
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const formattedDate = `${year}/${month}/${day}`

      setFieldValue('dateOfBirth', formattedDate)
      setFieldTouched('dateOfBirth', true, false)

      // Trigger validation
      if (typeof formik.validateField === 'function') {
        formik.validateField('dateOfBirth')
      }

      // Close calendar after selection
      setIsVisible(false)
    } else {
      setFieldValue('dateOfBirth', '')
    }
  }

  const selectedDate = parseDate()

  // Calculate max date (18 years ago from today for typical age restriction)
  const maxDate = new Date()
  maxDate.setFullYear(maxDate.getFullYear() - 18)

  // Calculate min date (100 years ago)
  const minDate = new Date()
  minDate.setFullYear(minDate.getFullYear() - 100)

  // Format display date
  const formatDisplayDate = (date) => {
    if (!date) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const handleToggleCalendar = (e) => {
    if (disabled) return
    e.stopPropagation()
    setIsVisible(!isVisible)
  }

  return (
    <>
      {regFields?.dateOfBirth !== 0 && (
        <div className='date-of-birth-container-v2' ref={ref}>
          <input
            type='text'
            value={formatDisplayDate(selectedDate)}
            onClick={handleToggleCalendar}
            placeholder={languageData?.dateOfBirthPlaceholder || 'Date of Birth'}
            className={`date-picker-input ${hasError ? 'input-error' : ''}`.trim()}
            disabled={disabled}
            autoComplete='off'
            readOnly
          />
          <Icon name='calendar' className='calendar-icon' onClick={handleToggleCalendar} />

          {isVisible && (
            <div className='calendar-popup'>
              <Calendar
                date={selectedDate || maxDate}
                onChange={handleDateChange}
                maxDate={maxDate}
                minDate={minDate}
                color='var(--input-border-color-primary)'
                locale={dateLocale || undefined}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default DateBirthV2
